import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { Title } from '../Title/Title';
import { Button } from '../Button/Button';
import Triangle from '../Triangle/Triangle';
import BlogPost from './components/BlogPost';
import BlogPostSkeleton from './components/BlogPostSkeleton';
import { Post } from '../../../../../models/blog';
import Carousel from '../Carousel/Carousel';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const fetchPopularPosts = async (): Promise<Post[]> => {
  const url =
    'https://blog.oncampusabroad.com/wp-json/wordpress-popular-posts/v1/popular-posts?range=all&limit=6';
  const response = await axios.get<Post[]>(url);
  return response.data;
};

const Blog = forwardRef<HTMLDivElement, { onLoad: () => void }>(
  ({ onLoad }, ref) => {
    const title = 'Most popular on our blog';
    const textButton = 'Visit OnCampus Abroad Blog!';
    const hrefButton = 'https://blog.oncampusabroad.com/';
    const buttonId = 'popular_visit_button';
    const [posts, setPosts] = React.useState<Post[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const { t } = useTranslation();

    React.useEffect(() => {
      const getPosts = async () => {
        setIsLoading(true);
        try {
          const data = await fetchPopularPosts();
          setPosts(data);
          onLoad();
        } finally {
          setIsLoading(false);
        }
      };
      getPosts();
    }, [onLoad]);
    if (!isLoading && posts.length === 0) return null;
    return (
      <Background ref={ref}>
        <Triangle color='white' position='right' />

        <Title text={title} />
        <Wrapper>
          <Carousel
            gap={12}
            slidesToShow={{ base: 1, sm: 2, lg: 3 }}
            ariaLabel={t('landing.blog.title')}
            navButtonLeftId='blog_carousel_navbutton_left'
            navButtonRightId='blog_carousel_navbutton_right'
          >
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <BlogPostSkeleton key={i} />
                ))
              : posts.map((post, index) => (
                  <BlogPost
                    key={post.id}
                    post={post}
                    isEven={index % 2 === 1}
                  />
                ))}
          </Carousel>
        </Wrapper>
        <ButtonBox>
          <Button href={hrefButton} newTab maxWidth='354px' id={buttonId}>
            {textButton}
          </Button>
        </ButtonBox>

        <Triangle color='white' orientation='bottom' position='right' />
      </Background>
    );
  }
);

export default Blog;
const Wrapper = styled.div`
  max-width: 1350px;
  width: 100%;
  padding: 45px 0;
  @media (min-width: 768px) {
    padding: 69px 0;
  }
`;
const ButtonBox = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;
const Background = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  padding: 2rem 1rem;
  position: relative;
  padding-bottom: 80px;
  margin-top: 80px;
  scroll-margin-top: 190px;

  @media (min-width: 500px) {
    padding-bottom: 120px;
  }

  @media (min-width: 650px) {
    padding: 4rem 2rem;
    padding-bottom: 180px;
  }

  @media (min-width: 768px) {
    padding-bottom: 200px;
  }
`;
